/*
 * Copyright (c) Nexign, JSC, 2020-2024.
 */
/**
 * The component for set wizard notification report
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.2.0
 */

import { Fragment, ReactNode, useCallback, useEffect } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { EmailInput } from '../../../../components/common/EmailInput/EmailInput';
import { PhoneInput } from '../../../../components/common/PhoneInput/PhoneInput';
import { IRootReducer } from '../../../../reducers/rootReducer';
import { makeSelectTranslationCommon, makeSelectWizardTranslation } from '../../../internationalization/intSelectors';
import ICommonTranslation from '../../../internationalization/types/common';
import { IWizardReportTranslation, IWizardTranslation } from '../../../internationalization/types/wizards/wizard';
import { wizardReportSetParamAction } from '../../wizardActions';
import { IWizardReportSetParamPayload } from '../../wizardTypes';
import {
    WizardReportContainerStyled,
    WizardReportHeaderStyled,
    WizardReportTitleStyled,
} from './styled/WizardReportStyled';
import { EWizardReportParam } from './WizardReportConstants';
import { WizardReportParam } from './WizardReportParam';
import { makeSelectWizardReportParam } from './wizardReportSelectors';
import { IWizardReportParam } from './wizardReportTypes';

interface IWizardReport {
    children?: ReactNode;
    isReportSubscribers?: boolean;
    notificationValidate: () => void;
    onChangeParam?: (payload: IWizardReportSetParamPayload) => void;
    wizardReportTranslation?: IWizardReportTranslation;
}

export type TWizardReport = IWizardReport & IConnectedProps & IDispatchProps;

export const WizardReport = (props: TWizardReport) => {
    const {
        children,
        commonTranslation,
        isReportSubscribers,
        reportEmail,
        reportPhone,
        subsNotificationNeeded,
        wizardTranslation,
        wizardReportTranslation,
        // actions
        notificationValidate,
        onChangeParam,
        setNotificationParam,
    } = props;

    useEffect(() => {
        notificationValidate();
    }, [notificationValidate]);

    useEffect(() => {
        notificationValidate();
    }, [notificationValidate, reportEmail, reportPhone]);

    const handleChange = useCallback(
        (payload: IWizardReportSetParamPayload) => {
            setNotificationParam(payload);
            if (onChangeParam) onChangeParam(payload);
        },
        [onChangeParam, setNotificationParam],
    );

    const getWizardReportTranslation = useCallback(
        (): IWizardReportTranslation => wizardReportTranslation || wizardTranslation.wizardReport,
        [wizardReportTranslation, wizardTranslation],
    );

    return (
        <WizardReportContainerStyled>
            <WizardReportTitleStyled>{getWizardReportTranslation().header}</WizardReportTitleStyled>

            {!!isReportSubscribers && (
                <Fragment>
                    <WizardReportHeaderStyled>{getWizardReportTranslation().toSubscribers}</WizardReportHeaderStyled>
                    <WizardReportParam
                        isEnabled={subsNotificationNeeded.isEnabled}
                        label={getWizardReportTranslation().phone}
                        setEnabled={(isEnabled: boolean) =>
                            handleChange({
                                paramType: EWizardReportParam.SUBS_NOTIFICATION_NEEDED,
                                isEnabled,
                            })
                        }
                    />
                </Fragment>
            )}

            <WizardReportHeaderStyled>{getWizardReportTranslation().toUser}</WizardReportHeaderStyled>

            <WizardReportParam
                dataName="wizard-report-param-email"
                isEnabled={reportEmail.isEnabled}
                label={getWizardReportTranslation().email}
                setEnabled={(isEnabled: boolean) =>
                    handleChange({
                        paramType: EWizardReportParam.EMAIL,
                        isEnabled,
                    })
                }
            >
                <EmailInput
                    emailInputValue={reportEmail.value}
                    isValid={reportEmail.isValid}
                    translation={commonTranslation}
                    onEmailInputChange={(value: string) =>
                        handleChange({
                            paramType: EWizardReportParam.EMAIL,
                            value,
                        })
                    }
                    onEmailInputValidate={(isValid: boolean) =>
                        handleChange({
                            paramType: EWizardReportParam.EMAIL,
                            isValid,
                        })
                    }
                />
            </WizardReportParam>

            <WizardReportParam
                dataName="wizard-report-param-phone"
                isEnabled={reportPhone.isEnabled}
                label={getWizardReportTranslation().phone}
                setEnabled={(isEnabled: boolean) =>
                    handleChange({
                        paramType: EWizardReportParam.PHONE,
                        isEnabled,
                    })
                }
            >
                <PhoneInput
                    isValid={reportPhone.isValid}
                    phoneInputValue={reportPhone.value}
                    phoneMask={commonTranslation.phoneInput.mask}
                    translation={commonTranslation}
                    onPhoneInputChange={(value: string) =>
                        handleChange({
                            paramType: EWizardReportParam.PHONE,
                            value,
                        })
                    }
                    onPhoneInputValidate={(isValid: boolean) =>
                        handleChange({
                            paramType: EWizardReportParam.PHONE,
                            isValid,
                        })
                    }
                />
            </WizardReportParam>

            {children}
        </WizardReportContainerStyled>
    );
};

interface IConnectedProps {
    commonTranslation: ICommonTranslation;
    reportEmail: IWizardReportParam;
    reportPhone: IWizardReportParam;
    subsNotificationNeeded: IWizardReportParam;
    wizardTranslation: IWizardTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    commonTranslation: makeSelectTranslationCommon(),
    reportEmail: makeSelectWizardReportParam(EWizardReportParam.EMAIL),
    reportPhone: makeSelectWizardReportParam(EWizardReportParam.PHONE),
    subsNotificationNeeded: makeSelectWizardReportParam(EWizardReportParam.SUBS_NOTIFICATION_NEEDED),
    wizardTranslation: makeSelectWizardTranslation(),
});

interface IDispatchProps {
    setNotificationParam: (payload: IWizardReportSetParamPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setNotificationParam: (payload) => dispatch(wizardReportSetParamAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardReport);
